import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { errMsg } from "@/lib/api";

const Input = ({ icon: Icon, id, ...p }) => (
  <label className="flex items-center gap-3 h-12 px-4 rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-[#B91C1C] transition-shadow">
    <Icon className="w-4 h-4 text-slate-500" />
    <input data-testid={id} className="flex-1 bg-transparent outline-none text-sm" {...p} />
  </label>
);

export default function LoginForm({ mode }) {
  const isUp = mode === "sign-up";
  const { token, login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (token) return <Navigate to="/panel" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isUp) await register(f.name, f.email, f.password); else await login(f.email, f.password);
      nav("/panel", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form onSubmit={submit} data-testid={`${mode}-form`} initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-sm bg-white rounded-2xl border shadow-[0_20px_60px_-20px_rgba(20,83,45,0.25)] p-7 space-y-4">
      <div>
        <h2 className="font-display text-2xl font-extrabold">{isUp ? "Daftar Pekerja" : "Log Masuk"}</h2>
        <p className="text-sm text-slate-500 mt-1">{isUp ? "Akaun baharu perlu diluluskan oleh admin." : "Masuk ke Panel Pekerja POKYA."}</p>
      </div>
      {isUp && <Input icon={User} id="auth-name-input" placeholder="Nama" value={f.name} onChange={set("name")} />}
      <Input icon={Mail} id="auth-email-input" type="email" required placeholder="Emel" value={f.email} onChange={set("email")} autoComplete="email" />
      <Input icon={Lock} id="auth-password-input" type="password" required minLength={isUp ? 6 : 1} placeholder="Kata laluan" value={f.password} onChange={set("password")}
        autoComplete={isUp ? "new-password" : "current-password"} />
      <motion.button whileTap={{ scale: 0.97 }} disabled={busy} data-testid="auth-submit-button"
        className="w-full h-12 rounded-xl bg-[#B91C1C] hover:bg-[#7F1D1D] text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-70">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}{isUp ? "Daftar" : "Log Masuk"}
      </motion.button>
      <p className="text-sm text-center text-slate-600">
        {isUp ? "Sudah ada akaun? " : "Belum ada akaun? "}
        <Link to={isUp ? "/sign-in" : "/sign-up"} data-testid="auth-switch-link" className="text-[#B91C1C] font-semibold hover:underline">{isUp ? "Log masuk" : "Daftar"}</Link>
      </p>
      <Link to="/" data-testid="auth-home-link" className="block text-xs text-center text-slate-400 hover:text-slate-600">← Kembali ke Menu</Link>
    </motion.form>
  );
}
